<template>
  <header class="top-bar">
    <div class="top-bar__left">
      <button
        v-if="showMenuButton"
        type="button"
        class="header-action top-bar__menu-button"
        @click="emit('toggleMenu')"
        :aria-label="t('accessibility.toggleMenu')"
        :aria-expanded="menuOpen"
      >
        <HamburgerIcon :open="menuOpen" />
      </button>

      <div v-if="breadcrumbs.length > 0" class="top-bar__breadcrumbs">
        <Breadcrumbs :items="breadcrumbs" />
      </div>
    </div>

    <div class="top-bar__center" :class="{ 'top-bar__center--expanded': isSearchExpanded }">
      <ClientSearchBar v-if="canSearchClients" ref="searchBar" />
    </div>

    <div class="top-bar__right">
      <button
        v-if="canSearchClients"
        type="button"
        class="header-action top-bar__search-toggle"
        :aria-label="t('accessibility.toggleClientSearch')"
        :aria-expanded="isSearchExpanded"
        @click="toggleSearch"
      >
        <Search :size="20" aria-hidden="true" />
      </button>
      <HelpButton />
      <NotificationBell v-if="hasOrganization" />
      <UserMenu />
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Search } from 'lucide-vue-next'
import { useBreadcrumbs } from '@/composables/useBreadcrumbs'
import { usePermissions } from '@/composables/usePermissions'
import { useUserProfileStore } from '@/stores/userProfile'
import { Permission } from '@/utils/permissionBitmask'
import HamburgerIcon from '@/components/atoms/HamburgerIcon.vue'
import Breadcrumbs from '@/components/molecules/Breadcrumbs.vue'
import ClientSearchBar from '@/components/organisms/ClientSearchBar.vue'
import HelpButton from '@/components/molecules/HelpButton.vue'
import NotificationBell from '@/components/molecules/NotificationBell.vue'
import UserMenu from '@/components/molecules/UserMenu.vue'

interface Props {
  showMenuButton?: boolean
  /**
   * Whether the side menu the button toggles is currently open. It drives
   * `aria-expanded` and the hamburger's morph into an X — the button stays in
   * the row and stays operable once the menu is open, so it has to say which
   * press does what (#4085).
   */
  menuOpen?: boolean
}

withDefaults(defineProps<Props>(), {
  showMenuButton: false,
  menuOpen: false
})

const emit = defineEmits<{
  toggleMenu: []
}>()

const { t } = useI18n()
const route = useRoute()
const { breadcrumbs } = useBreadcrumbs()
const { hasPermission } = usePermissions()
const userProfileStore = useUserProfileStore()

const canSearchClients = computed(() => hasPermission(Permission.CLIENTS_READ))
const hasOrganization = computed(() => userProfileStore.profile?.organizationId != null)

const searchBar = ref<InstanceType<typeof ClientSearchBar> | null>(null)

/**
 * Only read below `--breakpoint-md-down`, where the row cannot hold a usable
 * input beside the hamburger, the breadcrumbs and the three actions. The bar is
 * in the DOM at every width — it is laid out by CSS alone — so the toggle is
 * what makes it reachable on a phone rather than what makes it exist (#4072).
 */
const isSearchExpanded = ref(false)

async function toggleSearch() {
  isSearchExpanded.value = !isSearchExpanded.value
  if (!isSearchExpanded.value) return
  // The row is `display: none` until the class lands, and a hidden input cannot
  // take focus — so the focus has to wait for the render that shows it.
  await nextTick()
  searchBar.value?.focus()
}

// Picking a result navigates; the row it was picked from has said what it had to.
watch(() => route.fullPath, () => {
  isSearchExpanded.value = false
})
</script>

<!-- The 44x44 outlined box every action in this row draws (#4080). -->
<style src="@/assets/styles/header-action.css"></style>

<style scoped>
.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-md);
  height: 64px;
  background: var(--color-background-content);
  border-bottom: 1px solid var(--color-border-default);
  padding: 0 var(--spacing-lg);
  position: sticky;
  top: 0;
  z-index: var(--z-fixed);
  box-shadow: var(--shadow-sm);
}

.top-bar__left {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  flex: 1;
  min-width: 0;
}

.top-bar__center {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  padding: 0 var(--spacing-lg);
}

.top-bar__right {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  /* Size to the action buttons' content rather than claiming half the bar, but
     stay shrinkable so the breadcrumbs (left) yield space before the actions are
     pushed off the right edge on narrow screens. */
  flex: 0 1 auto;
  min-width: 0;
  justify-content: flex-end;
}

/*
 * The box is `.header-action`'s and the bars inside it are `HamburgerIcon`'s;
 * all that is left here is where the button appears. `display` restates a
 * property the sheet declares because this button is hidden above lg — a
 * caller's single class inside a scoped block outranks the sheet, which is what
 * makes that work.
 */
.top-bar__menu-button {
  display: none;
}

/* Offered only where the bar itself has no room in the row — see md-down. */
.top-bar__search-toggle {
  display: none;
}

/*
 * Below lg the bar has to make room for the hamburger. It used to make that room
 * by taking the cliëntenzoekbalk with it, which left a Chrome window at half
 * screen on a 1920 display — 960px — with no search at all (#4072). The centre
 * takes a share of the row instead and shrinks into it; `.client-search-bar`
 * gives up its 400px floor at the same width.
 */
@media (--breakpoint-lg-down) {
  .top-bar__menu-button {
    display: flex;
  }

  .top-bar__center {
    flex: 1 1 auto;
    min-width: 0;
    padding: 0 var(--spacing-sm);
  }
}

.top-bar__breadcrumbs {
  display: flex;
  align-items: center;
  flex: 1;
  /* Allow the breadcrumbs to truncate instead of pushing the action buttons
     off-screen when horizontal space runs out. */
  min-width: 0;
  overflow: hidden;
}

/*
 * On a phone the row genuinely cannot hold a usable input as well, so the bar
 * moves to a row of its own behind the toggle rather than being squeezed to
 * nothing — reachable, which is what it was not before.
 */
@media (--breakpoint-md-down) {
  .top-bar {
    padding: 0 var(--spacing-md);
    gap: var(--spacing-sm);
  }

  .top-bar__search-toggle {
    display: flex;
  }

  .top-bar__center {
    display: none;
    padding: 0;
  }

  /* Anchored to the sticky header, so the results stay placed against the bar. */
  .top-bar__center--expanded {
    display: flex;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    padding: var(--spacing-sm) var(--spacing-md);
    background: var(--color-background-content);
    border-bottom: 1px solid var(--color-border-default);
    box-shadow: var(--shadow-sm);
  }
}
</style>
